import { DashboardCustomizeOutlined, Sort, ViewAgendaOutlined, ViewModuleOutlined } from '@mui/icons-material'
import { MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PartHistory } from '../configuration/parts/partHistory'
import { apiGet, apiPut } from '../generic/Api_Functions'
import { useSmartInterval } from '../generic/hooks/useSmartInterval'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import ArrangeDevices from '../routes/ArrangeDevices'
import createDialogTitle from '../generic/utilities/dialogUtil'
import AlertUI from '../generic/AlertUI'
import useAuthorization from '../generic/hooks/useAuthorization/useAuthorization'
import PartProgressTwoLevelRail from '../generic/partProgress/PartProgressTwoLevelRail'
import useUser from '../generic/hooks/useUser/useUser'
import { groupErrorsByDeviceGroupId, getHeaderErrors } from './sensorAlarmUtils'
import { fetchMonitoringSnapshot } from './monitoringSnapshotAdapter'
import { fetchDeviceMainImages } from './deviceMainImageAdapter'
import { ALL_GROUPS_ID, QUICK_FILTERS, buildDeviceGroups, selectVisibleDevices } from './plantMonitoringUtils'
import PlantKpiBar from './PlantKpiBar'
import DeviceGroupNavigator from './DeviceGroupNavigator'
import DeviceGroupCompactCard from './DeviceGroupCompactCard'
import AssetGrid from './AssetGrid'
import AssetDetail from './AssetDetail'
import './byGroup.css'

const VIEW_MODE_STORAGE_KEY = 'monitoring.byGroup.viewMode'

// Merges a fresh snapshot device tree into the previous one PRESERVING object identity
// for every entry whose content did not change (deep-compared per item, one level of
// GROUP nesting). While the cell is running the snapshot carries a full payload every
// tick even if only one machine moved; preserving identities lets the memoized cards
// re-render only for the devices that really changed instead of all of them.
const mergeDevicesPreservingIdentity = (prev, next) => {
  if (prev === next) return prev
  const keyOf = (item) => `${item?.type ?? 'ITEM'}-${item?.id}`
  const prevByKey = new Map((prev ?? []).map((item) => [keyOf(item), item]))
  let anyChanged = (prev ?? []).length !== (next ?? []).length
  const merged = (next ?? []).map((item) => {
    const prevItem = prevByKey.get(keyOf(item))
    if (!prevItem) {
      anyChanged = true
      return item
    }
    if (JSON.stringify(prevItem) === JSON.stringify(item)) return prevItem
    anyChanged = true
    if (item?.type !== 'GROUP') return item
    // Changed GROUP: still keep the identity of its unchanged nested devices, so one
    // busy machine does not re-render its whole group's cards.
    const prevDevices = new Map((prevItem.devices ?? []).map((device) => [device.id, device]))
    return {
      ...item,
      devices: (item.devices ?? []).map((device) => {
        const prevDevice = prevDevices.get(device.id)
        return prevDevice && JSON.stringify(prevDevice) === JSON.stringify(device) ? prevDevice : device
      })
    }
  })
  return anyChanged ? merged : prev
}

/**
 * MonitoringScreenByGroup — MIC-4128 block A redesign (HU-04/05/06) of the Monitoring
 * screen, organised by group: plant-wide KPI bar (CA3), group navigator (CA1), quick
 * filters (CA2) and an asset grid filtered 100% in client (RN8/RN9), plus a compact
 * card for the selected group (HU-06, CA8) and an asset detail dialog (HU-05, CA6).
 *
 * CA9 (no operative regression): every existing action — parts, send program, move,
 * manage location, documents, calendar, traceability, arrange — is preserved by reusing
 * the shared `AssetCardDialogs`/`useAssetLiveData` (via the by-group cards) plus
 * `ArrangeDevices` and `PartHistory`, under their current permissions (RN17/RN18),
 * unchanged.
 *
 * Routing (convergence done, S13): BOTH `/home` and `/monitoring` mount this component
 * (`MainContainer.jsx`). The previous `MonitoringScreen` and its exclusive components
 * (`GroupCard`, `AssetCard`, `AssetCardView`) were removed once the PO validated the
 * redesign; there is no longer any pre-snapshot per-endpoint fallback either.
 *
 * Context restoration (RN19/CA7, S29/S30): the selected group/filter are reflected in
 * the URL query string (`?group=&filter=`) so `AssetCardDialogs`' "move resource" flow —
 * which reads `location.pathname + location.search` for its `urlBack` — naturally
 * restores this exact group/filter without any component needing to know about it.
 * Opening the asset detail never navigates away (it is an in-place dialog), so
 * returning from it trivially preserves the whole screen state (S29).
 */
export default function MonitoringScreenByGroup ({ partProgressVariant = '' }) {
  const { user } = useUser()
  const { t_ } = useSmartTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isUserAuthorized = useAuthorization()
  const [AlertElement, showAlert] = AlertUI()

  const initialParams = new URLSearchParams(location.search)

  const [devices, setDevices] = useState([])
  const [actualLocations, setActualLocations] = useState({})
  const [actualLocationsPartProgress, setActualLocationsPartProgress] = useState([])
  // Device main images (story 35): resolved in ONE batch call for all visible devices
  // (sun contract §9 — never one request per card), keyed by deviceId. The refresh token
  // is bumped when a main image is marked/removed (via `refreshMainImages`) and when the
  // detail closes, so a just-marked image is re-fetched even when REPLACING an existing one.
  const [deviceMainImages, setDeviceMainImages] = useState({})
  const [mainImagesRefreshToken, setMainImagesRefreshToken] = useState(0)
  // Stable identity: handed down to every (memoized) card — an inline arrow here would
  // defeat their React.memo on each screen render.
  const refreshMainImages = useCallback(() => setMainImagesRefreshToken((token) => token + 1), [])
  const [partProgressId, setPartProgressId] = useState('')
  const [partProgressSteps, setPartProgressSteps] = useState([])
  const [sensorErrorsByGroup, setSensorErrorsByGroup] = useState({})
  const [kpis, setKpis] = useState(undefined)
  const [refreshToken, setRefreshToken] = useState(0)
  const [selectedGroupId, setSelectedCellId] = useState(initialParams.get('group') || ALL_GROUPS_ID)
  const [filterKey, setFilterKey] = useState(initialParams.get('filter') || QUICK_FILTERS.ALL)
  const [detailDevice, setDetailDevice] = useState(null)
  const [renderHistoryComponent, setRenderHistoryComponent] = useState(null)
  const [showResourceHistory, setShowResourceHistory] = useState(false)
  const [showArrangeDialog, setShowArrangeDialog] = useState(false)
  const [arrangeDevices, setArrangeDevices] = useState([])
  // Card density (compact showroom vs dense production), remembered per user in
  // localStorage so a supervisor's chosen density survives reloads (PO).
  const [viewMode, setViewMode] = useState(() => {
    try { return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'production' ? 'production' : 'compact' } catch { return 'compact' }
  })

  useEffect(() => {
    try { window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode) } catch { /* storage unavailable — non-fatal */ }
  }, [viewMode])

  // Snapshot path (sun-contract section 6.1/6.4, built by sun in PR sun#3522):
  // `snapshotVersionRef` echoes sun's opaque change-detection token back on every
  // tick; `lastSnapshotRef` keeps the last full photo so an `unchanged` answer
  // costs nothing. `fixtureLocations/actual` still travels separately — the
  // snapshot does not embed it yet (contract gap recorded in the story).
  const snapshotVersionRef = useRef('')
  const snapshotPartIdRef = useRef('')
  const lastSnapshotRef = useRef({ devices: [], sensorErrors: [], plantKpis: undefined })
  // Physical locations (fixtureLocations/actual): sun embeds them in the snapshot under
  // the same token (contract §6 addendum). `lastLocationsRef` caches them so `unchanged`
  // ticks reuse them; only when a body snapshot omits the field (sun degradation) do we
  // fall back to the standalone endpoint.
  const lastLocationsRef = useRef([])
  // Derived-state caches keyed by SOURCE identity: on `unchanged` ticks the snapshot
  // hands back the same cached arrays, so re-deriving fresh maps/arrays would change
  // state identity and re-render the whole tree every 2s for nothing. Deriving only
  // when the source reference changes lets every setState below bail out.
  const derivedLocationsRef = useRef({ source: undefined, withProgress: [], perDevice: {} })
  const groupedSensorErrorsRef = useRef({ source: undefined, grouped: {} })

  const getIntervalData = useCallback(async (newPartProgressId) => {
    if (!newPartProgressId) newPartProgressId = partProgressId

    // Changing the tracked part invalidates the token: sun's `progressToken` is
    // part-specific, so force a full payload carrying the new part's route.
    if (String(newPartProgressId ?? '') !== snapshotPartIdRef.current) {
      snapshotVersionRef.current = ''
      snapshotPartIdRef.current = String(newPartProgressId ?? '')
    }

    const snapshot = await fetchMonitoringSnapshot({ userId: user.id, version: snapshotVersionRef.current, partId: newPartProgressId })

    // Snapshot unavailable (network/5xx): keep the last good photo instead of blanking
    // the screen. Sun no longer exposes the pre-snapshot per-endpoint fallback
    // (deviceSummary/deviceGroupErrors/deviceinfo were removed), so there is nothing to
    // fall back to — the snapshot is the single source.
    if (snapshot.degraded) {
      const cached = lastSnapshotRef.current
      return { devices: cached.devices, physicalLocations: lastLocationsRef.current, sensorErrors: cached.sensorErrors, plantKpis: cached.plantKpis, progress: undefined, changed: false }
    }

    snapshotVersionRef.current = snapshot.version

    // Resolve physical locations (fixtureLocations):
    //  - `unchanged`      → reuse the cache (covered by the same token).
    //  - embedded field   → use it; the tick is a single call.
    //  - body but no field (sun could not compute them) → standalone endpoint fallback.
    let locs
    if (snapshot.unchanged) {
      locs = lastLocationsRef.current
    } else if (snapshot.physicalLocations !== undefined) {
      locs = snapshot.physicalLocations
    } else {
      locs = await apiGet('fixtureLocations/actual')
    }
    lastLocationsRef.current = locs

    if (snapshot.unchanged) {
      const cached = lastSnapshotRef.current
      // progress: undefined = "keep the route you already have" (sun omitted it).
      return { devices: cached.devices, physicalLocations: locs, sensorErrors: cached.sensorErrors, plantKpis: cached.plantKpis, progress: undefined, changed: false }
    }

    lastSnapshotRef.current = { devices: snapshot.devices, sensorErrors: snapshot.sensorErrors, plantKpis: snapshot.plantKpis }
    const progress = newPartProgressId === '' ? [] : snapshot.partProgress
    return { devices: snapshot.devices, physicalLocations: locs, sensorErrors: snapshot.sensorErrors, plantKpis: snapshot.plantKpis, progress, changed: true }
  }, [partProgressId, user.id])

  const setIntervalData = useCallback((intervalData) => {
    // Same reference on `unchanged`/degraded ticks — bail out without any deep compare;
    // on changed ticks preserve per-device identity so memoized cards only re-render
    // for the devices that really changed.
    setDevices((prev) => mergeDevicesPreservingIdentity(prev, intervalData.devices))

    const locationsSource = intervalData.physicalLocations ?? []
    if (derivedLocationsRef.current.source !== locationsSource) {
      const withProgress = locationsSource
        .filter((location) => location.showWorkflowSteps)
        .sort((a, b) => a.resources[0].code.localeCompare(b.resources[0].code))
      const perDevice = {}
      locationsSource.forEach((location) => {
        if (!(location.deviceId in perDevice)) perDevice[location.deviceId] = []
        perDevice[location.deviceId].push(location)
      })
      derivedLocationsRef.current = { source: locationsSource, withProgress, perDevice }
    }
    const actualLocationsWithProgress = derivedLocationsRef.current.withProgress
    setActualLocationsPartProgress(actualLocationsWithProgress)
    setActualLocations(derivedLocationsRef.current.perDevice)

    if (groupedSensorErrorsRef.current.source !== intervalData.sensorErrors) {
      groupedSensorErrorsRef.current = { source: intervalData.sensorErrors, grouped: groupErrorsByDeviceGroupId(intervalData.sensorErrors) }
    }
    setSensorErrorsByGroup(groupedSensorErrorsRef.current.grouped)

    // Part journey steps refresh on the same unified tick (no own polling): the
    // backend re-plans the remaining route after a FAIL/rework, so re-setting the
    // list here is what makes PartJourney show reroutes. `undefined` means the
    // snapshot omitted the field ("nothing changed, keep your route" — 6.1).
    if (intervalData.progress !== undefined) {
      setPartProgressSteps(Array.isArray(intervalData.progress) ? intervalData.progress : [])
    }

    setPartProgressId((current) => {
      if (current === '') return current
      const stillThere = actualLocationsWithProgress.some((location) => location.resources[0].id === current)
      return stillThere ? current : ''
    })

    // RN2/RN5: KPIs are only ever recalculated on this same periodic tick, never on
    // group/filter navigation (they ride the snapshot; see PlantKpiBar).
    setKpis(intervalData.plantKpis)

    // D4 (R2/S35), refined for performance: the unified refresh tick for the cards'
    // on-demand data (see useAssetLiveData.js) is bumped only when the snapshot carried
    // actual changes. `unchanged` ticks used to bump it too, re-rendering every card
    // (and, in production view, re-firing one deviceProduction request per card) every
    // 2s while nothing moved.
    if (intervalData.changed) setRefreshToken((previous) => previous + 1)
  }, [])

  const onPartProgressIdSelected = useCallback(async (newId) => {
    setPartProgressId(newId)
    const data = await getIntervalData(newId)
    setIntervalData(data)
  }, [getIntervalData, setIntervalData])

  // Real-time monitoring (PO): short 2s tick so status/parts changes surface fast. The
  // snapshot is cheap on unchanged ticks (treeHash → 200 {unchanged}); the per-tick cost
  // is mainly `fixtureLocations/actual`. Lower further only if the backend tolerates it.
  useSmartInterval(getIntervalData, 2000, setIntervalData)

  // Flat list of every visible device id (top-level + nested inside GROUP items), used
  // to batch-fetch their main images.
  const allDeviceIdsRef = useRef([])
  const allDeviceIds = useMemo(() => {
    const ids = []
    for (const item of devices) {
      if (item?.type === 'GROUP') {
        (item.devices ?? []).forEach((groupDevice) => { if (groupDevice?.id !== undefined) ids.push(groupDevice.id) })
      } else if (item?.id !== undefined) {
        ids.push(item.id)
      }
    }
    // Same id set → keep the previous array identity: `devices` changes on every busy
    // tick (liveInfo churn), and without this the main-images batch effect below would
    // re-fire its POST every 2s while the cell runs, for an id set that never changed.
    if (ids.join(',') === allDeviceIdsRef.current.join(',')) return allDeviceIdsRef.current
    allDeviceIdsRef.current = ids
    return ids
  }, [devices])

  // One batch call per change of the visible device set (not per tick): main images
  // change rarely, so this avoids a per-tick cost while keeping the map fresh when the
  // grid/filter changes. Each card/detail then loads only its own thumbnail if it has one.
  useEffect(() => {
    if (allDeviceIds.length === 0) {
      setDeviceMainImages({})
      return
    }
    let isCurrent = true
    fetchDeviceMainImages(allDeviceIds).then((map) => { if (isCurrent) setDeviceMainImages(map) })
    return () => { isCurrent = false }
  }, [allDeviceIds, mainImagesRefreshToken])

  useEffect(() => {
    if (partProgressId === '' && actualLocationsPartProgress.length > 0) {
      if (actualLocationsPartProgress.filter((actualLocation) => actualLocation.resources[0].id === partProgressId).length === 0) {
        onPartProgressIdSelected(actualLocationsPartProgress[0].resources[0].id)
      }
    }
  }, [actualLocationsPartProgress, partProgressId, onPartProgressIdSelected])

  // Purely presentational decoration (device count + alarm accent) for
  // DeviceGroupNavigator's rail — RN9 stays 100% client-side, no extra fetch involved.
  const deviceGroups = useMemo(() => buildDeviceGroups(devices).map((group) => {
    if (group.type !== 'GROUP') return group
    const sourceGroup = devices.find((item) => item.type === 'GROUP' && item.id === group.id)
    return {
      ...group,
      groupType: sourceGroup?.groupType,
      deviceCount: (sourceGroup?.devices ?? []).length,
      hasAlarm: getHeaderErrors(sensorErrorsByGroup[String(group.id)] ?? []).length > 0
    }
  }), [devices, sensorErrorsByGroup])
  // Framed section per device group (header: name + visible count + alarm dot),
  // the same distinction the old /monitoring screen made with GroupCard (PO
  // feedback). Unified for both views: "all groups" renders one section per group
  // (plus "no group"), a selected group renders just its own section — same frame,
  // no separate compact card (PO iteration).
  const groupSections = useMemo(() => {
    const showAll = String(selectedGroupId) === String(ALL_GROUPS_ID)
    return deviceGroups
      .filter((group) => showAll || String(group.id) === String(selectedGroupId))
      .map((group) => ({
        group,
        groupDevices: selectVisibleDevices(devices, group.id, filterKey),
        // Type summary/alarms run over the WHOLE group, not the filtered subset
        allGroupDevices: selectVisibleDevices(devices, group.id, QUICK_FILTERS.ALL),
        sensorErrors: group.type === 'GROUP' ? sensorErrorsByGroup[String(group.id)] ?? [] : []
      }))
      .filter((section) => section.groupDevices.length > 0)
  }, [deviceGroups, devices, filterKey, selectedGroupId, sensorErrorsByGroup])

  // Presentational only (S9/S10/RN2 unaffected: no extra fetch, no KPI involved) —
  // integrated counters for the quick-filter pills (mockup "All Running 18 / Faulted
  // Only 2 / Idle 4") and the "current context" title, both scoped to the selected
  // group just like the grid itself (RN9).
  const filterCounts = useMemo(() => ({
    [QUICK_FILTERS.ALL]: selectVisibleDevices(devices, selectedGroupId, QUICK_FILTERS.ALL).length,
    [QUICK_FILTERS.WORKING]: selectVisibleDevices(devices, selectedGroupId, QUICK_FILTERS.WORKING).length,
    [QUICK_FILTERS.IDLE]: selectVisibleDevices(devices, selectedGroupId, QUICK_FILTERS.IDLE).length,
    [QUICK_FILTERS.ERROR]: selectVisibleDevices(devices, selectedGroupId, QUICK_FILTERS.ERROR).length,
    [QUICK_FILTERS.OFF]: selectVisibleDevices(devices, selectedGroupId, QUICK_FILTERS.OFF).length
  }), [devices, selectedGroupId])

  // RN19/CA7/S29/S30: keep the URL in sync with the current group/filter so any
  // navigation away and back (e.g. AssetCardDialogs' "move resource") restores them.
  const updateUrlContext = useCallback((nextGroupId, nextFilterKey) => {
    const params = new URLSearchParams(location.search)
    params.set('group', nextGroupId)
    params.set('filter', nextFilterKey)
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
  }, [location.pathname, location.search, navigate])

  // Only re-run when the group/filter actually change, not on every location update
  // that `updateUrlContext` itself causes (a stable ref avoids adding it as a dep).
  const updateUrlContextRef = useRef(updateUrlContext)
  updateUrlContextRef.current = updateUrlContext
  useEffect(() => {
    updateUrlContextRef.current(selectedGroupId, filterKey)
  }, [selectedGroupId, filterKey])

  // Stable identity (t_ is react-i18next's stable `t`): handed down to every memoized
  // card via AssetGrid — an inline function would defeat their React.memo each render.
  const handleResourceHistory = useCallback((resource) => {
    if (resource?.type.name === 'Part') {
      setRenderHistoryComponent(<PartHistory partId={resource.id} />)
    } else {
      const resourceType = resource?.type.name
      setRenderHistoryComponent(
        <Typography variant='h6' sx={{ textAlign: 'center' }}>
          {t_('traceabilityNotAvailable', { resourceType: t_(resourceType.toLowerCase()) })}
        </Typography>
      )
    }
    setShowResourceHistory(true)
  }, [t_])

  const setVisibility = (deviceId) => {
    setArrangeDevices((current) => current.map((element) => {
      if (element.id === deviceId) return { ...element, visible: !element.visible }
      if (element.devices?.length > 0) {
        return {
          ...element,
          devices: element.devices.map((device) => device.id === deviceId ? { ...device, visible: !device.visible } : device)
        }
      }
      return element
    }))
  }

  const saveSorting = () => {
    apiPut('monitoring/arrange/update/' + user.id, arrangeDevices)
      .then(() => getIntervalData())
      .then((data) => {
        setIntervalData(data)
        showAlert({ severity: 'success', title: t_('Update successful'), message: t_('The operation was correctly performed') })
      })
      .catch((error) => {
        showAlert({
          title: t_('Update error'),
          severity: error.name,
          message: (
            <div>
              <div>{t_('If error persists, contact with support:')}</div>
              <div>{error.message}</div>
            </div>)
        })
      })
    setShowArrangeDialog(false)
  }

  return (
    <div className='mcx-root mcx-screen'>
      <SmartDialog
        setOpen={showResourceHistory}
        renderComponent={renderHistoryComponent}
        accept={false}
        close
        cancel={false}
        closeCallback={() => setShowResourceHistory(false)}
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('MONITORING ASSET ORDER'), icon: Sort })}
        setOpen={showArrangeDialog}
        renderComponent={<ArrangeDevices devices={arrangeDevices} setVisibility={setVisibility} sortingChange={setArrangeDevices} />}
        accept
        cancel
        cancelCallback={() => setShowArrangeDialog(false)}
        acceptCallback={() => saveSorting()}
      />
      {AlertElement}

      <div className='mcx-layout'>
        {/*
          Stitch dashboard sidebar: group navigation + quick filters live together
          (stitch-dashboard.html), still collapsible to a rail so the card grid can
          take the full width (earlier PO feedback, preserved).
        */}
        <DeviceGroupNavigator
          deviceGroups={deviceGroups}
          selectedGroupId={selectedGroupId}
          onSelect={setSelectedCellId}
          activeFilter={filterKey}
          onSelectFilter={setFilterKey}
          counts={filterCounts}
        />

        <main className='mcx-main'>
          {/*
            Part-tracking context header (PO iteration: no group info here — the
            group is already visible in the navigator): only the part selector and
            its progress rail, shown only while some part is being tracked.
          */}
          {actualLocationsPartProgress.length > 0 && (
            <section className='mcx-panel mcx-context'>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                {/* Tracked-part selector, restyled to the control-room language:
                    eyebrow micro-label + dark field with the module's outline and
                    primary accent, using the app font (same tokens as byGroup.css). */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className='mcx-eyebrow'>{t_('Part')}</span>
                  <TextField
                    select
                    size='small'
                    id='PartProgressSelect'
                    variant='outlined'
                    value={partProgressId}
                    onChange={(event) => onPartProgressIdSelected(event.target.value)}
                    sx={{
                      minWidth: 190,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#1d1d1d',
                        borderRadius: '4px',
                        fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        fontSize: 14,
                        color: '#7ae0a9',
                        '& fieldset': { borderColor: '#363636' },
                        '&:hover fieldset': { borderColor: '#7ae0a9' },
                        '&.Mui-focused fieldset': { borderColor: '#7ae0a9', borderWidth: 1 }
                      },
                      '& .MuiSvgIcon-root': { color: '#bbbbbb' }
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            bgcolor: '#282828',
                            border: '1px solid #363636',
                            borderRadius: '4px',
                            '& .MuiMenuItem-root': {
                              fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                              fontSize: 14,
                              color: '#f9f9f9'
                            },
                            '& .MuiMenuItem-root.Mui-selected': {
                              bgcolor: 'rgba(122, 224, 169, 0.12)',
                              color: '#7ae0a9'
                            }
                          }
                        }
                      }
                    }}
                  >
                    {actualLocationsPartProgress.map((actualLocation) => (
                      <MenuItem key={actualLocation.resources[0].id} value={actualLocation.resources[0].id}>
                        {actualLocation.resources[0].code}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PartProgressTwoLevelRail partProgress={partProgressSteps} />
                </div>
              </div>
            </section>
          )}

          <PlantKpiBar kpis={kpis} />

          {groupSections.length === 0
            ? (
              <p data-testid='asset-grid-empty' className='mcx-empty'>
                {t_('No assets match the selected filter')}
              </p>
              )
            : (
                groupSections.map(({ group, groupDevices, sensorErrors }) => (
                  <section
                    key={group.id}
                    data-testid={`group-section-${group.id}`}
                    className={`mcx-groupsection${group.hasAlarm ? ' mcx-groupsection--alarm' : ''}`}
                  >
                    {/* Single header line: name + count + severity counters with the
                        collapsible alarm list (old GroupCard pattern, HU-06) + type chips. */}
                    <DeviceGroupCompactCard
                      title={group.type === 'NO_GROUP' ? t_('No group') : group.name}
                      count={groupDevices.length}
                      sensorErrors={sensorErrors}
                    />
                    <AssetGrid
                      devices={groupDevices}
                      actualLocations={actualLocations}
                      mainImages={deviceMainImages}
                      onItemClick={handleResourceHistory}
                      onOpenDetail={setDetailDevice}
                      onMainImageChange={refreshMainImages}
                      // The tick token only drives per-card fetches in production view
                      // (useAssetLiveData ignores it otherwise): pinning it in compact
                      // view keeps the memoized cards from re-rendering on every tick.
                      refreshToken={viewMode === 'production' ? refreshToken : 0}
                      viewMode={viewMode}
                    />
                  </section>
                ))
              )}

          {/* Slim toolbar: card-density toggle (left) + Arrange (right), sticky at
              the bottom of the content column. */}
          <div className='mcx-viewtoggle'>
            <span className='mcx-viewtoggle__group'>
              <span className='mcx-eyebrow'>{t_('View')}</span>
              <ToggleButtonGroup
                exclusive
                value={viewMode}
                onChange={(event, next) => { if (next) setViewMode(next) }}
                sx={{
                  '& .MuiToggleButton-root': { color: 'var(--mcx-on-variant)', borderColor: 'var(--mcx-outline)', px: 2.5, py: 1.1 },
                  '& .Mui-selected': { color: 'var(--mcx-on-primary) !important', bgcolor: 'var(--mcx-primary-dim) !important' }
                }}
              >
                <ToggleButton value='compact' data-testid='view-mode-compact'>
                  <Tooltip title={t_('Compact')}><ViewModuleOutlined /></Tooltip>
                </ToggleButton>
                <ToggleButton value='production' data-testid='view-mode-production'>
                  <Tooltip title={t_('Production')}><ViewAgendaOutlined /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </span>
            <span style={{ flex: 1 }} />
            <button
              type='button'
              data-testid='arrange-pill'
              className='mcx-arrange-pill'
              disabled={!isUserAuthorized('ARRANGE')}
              onClick={async () => { setShowArrangeDialog(true); setArrangeDevices(await apiGet('monitoring/arrange/' + user.id)) }}
            >
              <DashboardCustomizeOutlined sx={{ fontSize: 16 }} />
              <span>{t_('Arrange')}</span>
            </button>
          </div>
        </main>
      </div>

      {detailDevice && (
        <AssetDetail
          device={detailDevice}
          locations={detailDevice.id in actualLocations ? actualLocations[detailDevice.id] : []}
          mainImageInfo={deviceMainImages[detailDevice.id]}
          open
          onClose={() => { setDetailDevice(null); refreshMainImages() }}
          onItemClick={handleResourceHistory}
          onMainImageChange={refreshMainImages}
        />
      )}
    </div>
  )
}
